import React, { useCallback, useEffect, useRef } from 'react'
import { ScrollView } from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { useFocusEffect } from '@react-navigation/native'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { GetMoniUsers, GetRemotyUsers, GetSigmaCloudUsers, GetSigmaCloudUsersParams, GetSowillUsers, GetSowillUsersParams } from '@/domain/usecases'
import { ClientMonitoringType } from '@/domain/models'
import { Box, CardList, PageHeader, Stack, ActivityIndicator, HStack, Text } from '@/presentation/components'
import { useAuth, useErrorHandler, useMediaQueries, useThemeClient } from '@/presentation/hooks'
import { theme } from '@/presentation/styles'
import { CardUser, userListState, isLoadingUserEventsState, SelectRemotyEventCompany, SelectSigmaCloudKey, sowillIdentifierState, SelectSowillKey, selectSigmaCloudKeyData, remotyFilterState, userListHasError, lastLoadedUserKeyState } from '@/presentation/pages/Events/components'
import { layoutStyles } from '@/presentation/styles/globals'
import { buildMonitoringTabRequestKey } from '../../request-key'

type UserEventListProps = {
  getMoniUsers: GetMoniUsers
  getRemotyUsers: GetRemotyUsers
  getSowillUsers: GetSowillUsers
  getSigmaCloudUsers: GetSigmaCloudUsers
}

export const UserEventList: React.FC<UserEventListProps> = (props) => {
  const { getClient } = useAuth()
  const handleError = useErrorHandler()
  const { isMobile } = useMediaQueries()
  const { getAccentColor } = useThemeClient()
  const client = getClient()

  const [hasError, setHasError] = useRecoilState(userListHasError)
  const { remotyIdEquipamento } = useRecoilValue(remotyFilterState)
  const currentSigmaKey = useRecoilValue(selectSigmaCloudKeyData)
  const [isLoading, setLoading] = useRecoilState(isLoadingUserEventsState)
  const [userList, setUserList] = useRecoilState(userListState)
  const lastLoadedKey = useRecoilValue(lastLoadedUserKeyState)
  const setLastLoadedKey = useSetRecoilState(lastLoadedUserKeyState)
  const sowillIdentifier = useRecoilValue(sowillIdentifierState)
  const previousRequestKeyRef = useRef('')

  const currentRequestKey = buildMonitoringTabRequestKey({
    clientType: client.tipoConexao,
    currentSigmaKey,
    remotyIdEquipamento,
    sowillIdentifier
  })

  useEffect(() => {
    if (
      hasError &&
      previousRequestKeyRef.current !== '' &&
      previousRequestKeyRef.current !== currentRequestKey
    ) {
      setHasError(false)
    }

    previousRequestKeyRef.current = currentRequestKey
  }, [currentRequestKey, hasError, setHasError])

  useFocusEffect(
    useCallback(() => {
      const conditions = {
        [ClientMonitoringType.REMOTY]: remotyIdEquipamento && remotyIdEquipamento !== 0,
        [ClientMonitoringType.MONI]: client.tipoConexao === 1,
        [ClientMonitoringType.SIGMA_CLOUD]: !!(currentSigmaKey.codCliente),
        [ClientMonitoringType.SIGMA_DESK]: !!(currentSigmaKey.codCliente),
        [ClientMonitoringType.SO_WILL]: !!(sowillIdentifier.codInternoChave)
      }[client.tipoConexao]

      if (conditions && !hasError && currentRequestKey !== lastLoadedKey) { getEventUser(currentRequestKey) }
    }, [
      client.codCliente,
      remotyIdEquipamento,
      currentSigmaKey,
      currentSigmaKey.codCliente,
      sowillIdentifier.codInternoChave,
      hasError,
      currentRequestKey,
      lastLoadedKey
    ])
  )

  const errorHandler = (error: Error): void => {
    handleError(error)
    setHasError(true)
  }

  const handleGetMoniUserList = (requestKey: string): void => {
    const params: GetMoniUsers.Params = {
      unidade: client.unidade,
      empresaMoni: client.empresaMonitoramento,
      codCliente: client.codCliente
    }

    if (requestKey !== lastLoadedKey) {
      setLoading(true)
    }

    props.getMoniUsers
      .getUsers(params)
      .then((users) => {
        setUserList(users)
        setLastLoadedKey(requestKey)
      })
      .catch(errorHandler)
      .finally(() => setLoading(false))
  }

  const handleGetRemotyUserList = (requestKey: string): void => {
    const params: GetRemotyUsers.Params = {
      idEquipamento: remotyIdEquipamento,
      empresaMonitoramento: client.empresaMonitoramento
    }

    if (requestKey !== lastLoadedKey) {
      setLoading(true)
    }

    props.getRemotyUsers
      .getUsers(params)
      .then((users) => {
        setUserList(users)
        setLastLoadedKey(requestKey)
      })
      .catch(errorHandler)
      .finally(() => setLoading(false))
  }

  const handleGetSowillUserList = (requestKey: string): void => {
    const params: GetSowillUsersParams = {
      idCliente: sowillIdentifier.idCliente,
      codInternoChave: sowillIdentifier.codInternoChave
    }

    if (requestKey !== lastLoadedKey) {
      setLoading(true)
    }

    props.getSowillUsers
      .getUsers(params)
      .then((users) => {
        setUserList(users)
        setLastLoadedKey(requestKey)
      })
      .catch(errorHandler)
      .finally(() => setLoading(false))
  }

  const handleGetSigmaCloudUserList = (requestKey: string): void => {
    const params: GetSigmaCloudUsersParams = {
      codCentral: currentSigmaKey.idMonitoramento,
      empresaSigma: currentSigmaKey.empresaSigma
    }

    if (requestKey !== lastLoadedKey) {
      setLoading(true)
    }

    props.getSigmaCloudUsers
      .getUsers(params)
      .then((users) => {
        setUserList(users)
        setLastLoadedKey(requestKey)
      })
      .catch(errorHandler)
      .finally(() => setLoading(false))
  }

  const getEventUser = {
    [ClientMonitoringType.MONI]: handleGetMoniUserList,
    [ClientMonitoringType.REMOTY]: handleGetRemotyUserList,
    [ClientMonitoringType.SIGMA_CLOUD]: handleGetSigmaCloudUserList,
    [ClientMonitoringType.SIGMA_DESK]: () => { },
    [ClientMonitoringType.SO_WILL]: handleGetSowillUserList
  }[client.tipoConexao]

  return (
    <Box style={{ ...layoutStyles.pageContent }}>
      <PageHeader
        text='Usuarios'
        icon={<MaterialCommunityIcons name='alarm-light-outline' size={24} color={theme.colors.grey} />}
        children={isLoading && (
          <HStack items='center' spacing={8}>
            <ActivityIndicator size='small' color={getAccentColor()} />
            <Text color={theme.colors.grey}>Buscando usuarios</Text>
          </HStack>
        )}
      />

      <Stack
        direction={isMobile ? 'column-reverse' : 'row'}
        mr={window.innerWidth <= 1380 ? 36 : isMobile ? 'auto' : 0}
        ml={window.innerWidth <= 1380 ? 36 : isMobile ? 'auto' : 0}
        justify='between'
        items='center'
        spacing={8}
      >
        {[ClientMonitoringType.REMOTY].includes(client.tipoConexao) && <SelectRemotyEventCompany disabled={isLoading} />}
        {[ClientMonitoringType.SIGMA_CLOUD].includes(client.tipoConexao) && <SelectSigmaCloudKey disabled={isLoading} />}
        {[ClientMonitoringType.SO_WILL].includes(client.tipoConexao) && <SelectSowillKey disabled={isLoading} />}
      </Stack>

      <ScrollView style={{ padding: '1rem' }}>
        <CardList
          isEmpty={!userList.length}
          isLoading={isLoading}
          textLoading='Buscando informações de usuarios'
        >
          {userList?.map(user => (
            <CardUser
              key={`cardUser-${user?.nome?.replaceAll(' ', '')}`}
              user={user}
            />
          ))}
        </CardList>
      </ScrollView>
    </Box>
  )
}
